import { PjmAgreementType, PjmGeneratorFuelType, PjmGeneratorPermitStatus, PjmGeneratorPermitType, PjmGeneratorType } from '../dto/enums';
import { PjmGeneratorInfo } from '../dto/PjmGeneratorInfo';

function makeDefaultGenerator(idx: number, maxOutput: number): PjmGeneratorInfo {
    return {
        name: `Generator ${idx + 1}`,
        nonRetailBtmg: true,
        maxOutput,
        nameplate: undefined,
        backupGeneratorOnly: true,
        generatorType: PjmGeneratorType.None,
        fuelType: PjmGeneratorFuelType.None,
        vintage: new Date().getFullYear(),
        retrofitYear: undefined,
        permitStatus: PjmGeneratorPermitStatus.NOT_APPLICABLE,
        permitType: PjmGeneratorPermitType.NONE,
        agreementType: PjmAgreementType.NONE,
        injectionRight: undefined,
        note: undefined,
        order202c: undefined,
    };
}

type GeneratorProps = {
    current: {
        generatorInfo: PjmGeneratorInfo[];
        generatorCount: number;
    };
    update: {
        generatorKw: number;
        generatorCount: number;
    };
};

export function makeGeneratorInfo({ current, update }: GeneratorProps): PjmGeneratorInfo[] {
    if (update.generatorCount === 0) {
        return [];
    }

    if (update.generatorKw < update.generatorCount) {
        throw new Error(`Cannot split generatorKw=${update.generatorKw} among ${update.generatorCount} generators as integer numbers`);
    }

    const currentTotalKw = current.generatorInfo.reduce((sum, g) => sum + g.maxOutput, 0);

    // Same count and same total kW → no changes needed
    if (current.generatorCount === update.generatorCount && currentTotalKw === update.generatorKw) {
        return current.generatorInfo;
    }

    const newGeneratorCount = update.generatorCount - current.generatorInfo.length;

    // When adding generators: keep existing maxOutput and assign remaining kW to new generators only.
    // Fall back to full redistribution only if remaining kW can't give each new generator ≥1 kW.
    if (newGeneratorCount > 0) {
        const remainingKw = update.generatorKw - currentTotalKw;
        if (remainingKw >= newGeneratorCount) {
            const result = [...current.generatorInfo];
            const newBase = Math.floor(remainingKw / newGeneratorCount);
            const newRemainder = remainingKw % newGeneratorCount;
            for (let idx = current.generatorInfo.length; idx < update.generatorCount; idx++) {
                const newIdx = idx - current.generatorInfo.length;
                result.push(makeDefaultGenerator(idx, newIdx < newRemainder ? newBase + 1 : newBase));
            }
            return result;
        }
    }

    // Fallback: redistribute kW across all generators (integer split).
    // Used when: count decreases, kW changes with same count, or new generators can't each get ≥1 kW.
    const base = Math.floor(update.generatorKw / update.generatorCount);
    const remainder = update.generatorKw % update.generatorCount;
    const kwForIndex = (idx: number) => (idx < remainder ? base + 1 : base);

    const kept = current.generatorInfo.slice(0, update.generatorCount).map((generator, idx) => ({
        ...generator,
        maxOutput: kwForIndex(idx),
    }));

    for (let idx = kept.length; idx < update.generatorCount; idx++) {
        kept.push(makeDefaultGenerator(idx, kwForIndex(idx)));
    }

    return kept;
}
